import { useState } from "react";
import { api, unwrap } from "../api/client";
import type { OutputView } from "../api/types";
import { useCan } from "../lib/auth";
import { formatDuration, formatNumber } from "../lib/format";
import { rich, t } from "../lib/i18n";
import { Button, ConfirmDialog, CopyField, Modal, useToast } from "./ui";

/** Sortie SRT listener : réglages à transmettre au client, clients connectés, nouvelle clé. */
export function ListenerModal({ output, onClose }: { output: OutputView; onClose: () => void }) {
  const toast = useToast();
  const can = useCan();
  const [renewing, setRenewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const access = output.listener;
  const clients = output.status.clients;

  const renew = async () => {
    setBusy(true);
    try {
      await unwrap(api.POST("/api/outputs/{output_id}/listener-key", { params: { path: { output_id: output.id } } }));
      toast(t("Nouvelle clé : transmettez le nouveau streamid au client"), "warn");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBusy(false);
      setRenewing(false);
    }
  };

  const kick = async (connId: string) => {
    try {
      await unwrap(api.POST("/api/outputs/{output_id}/clients/{conn_id}/kick",
        { params: { path: { output_id: output.id, conn_id: connId } } }));
      toast(t("Client déconnecté (il peut se reconnecter : changez la clé pour l'en empêcher)"), "warn");
    } catch (e) {
      toast((e as Error).message, "err");
    }
  };

  return (<>
    <Modal open wide title={t("Accès client · {name}", { name: output.name })} onClose={onClose}
      footer={<>
        {can("operator") && <Button onClick={() => setRenewing(true)} disabled={busy}>{t("Nouvelle clé")}</Button>}
        <Button variant="primary" onClick={onClose}>{t("Fermer")}</Button>
      </>}>
      {access && (
        <div className="flex flex-col gap-4">
          <p className="text-[13px] text-muted">
            {rich("Dans le décodeur du client : mode <b>caller</b>, puis ces réglages. Le flux n'est envoyé que lorsque la sortie est démarrée.", { b: (s) => <strong>{s}</strong> })}{!output.enabled && <span className="text-warn"> {t("Elle est arrêtée.")}</span>}
          </p>
          <div className="flex flex-col gap-1.5">
            <CopyField label={t("hôte")} value={access.host} />
            <CopyField label="port" value={String(access.port)} />
            <CopyField label="stream" value={access.streamid} />
            {access.encrypted && output.srt_passphrase && <CopyField label="pass" value={output.srt_passphrase} />}
            <CopyField label="url" value={access.url} />
          </div>
          <p className="text-[12px] text-faint">
            {access.encrypted ? t("Liaison chiffrée : le client doit saisir la passphrase.") : t("Liaison non chiffrée.")}{" "}
            {t("Le streamid contient la clé de la sortie ; « Nouvelle clé » déconnecte les clients et refuse l'ancienne. Changer la source (routage, secours) déconnecte aussi les clients une à deux secondes : un décodeur SRT se reconnecte de lui-même. Avec un nom d'hôte ou une adresse publique différente, remplacez l'hôte.")}
          </p>

          <div>
            <h3 className="mb-2 font-cond text-[12px] font-semibold uppercase tracking-wider text-faint">
              {t("Clients connectés ({n})", { n: clients.length })}
            </h3>
            {clients.length === 0 ? (
              <p className="text-[13px] text-muted">{t("Aucun client connecté.")}</p>
            ) : (
              <div className="overflow-x-auto rounded border border-line">
                <table className="w-full min-w-[560px] text-left text-[13px]">
                  <thead className="border-b border-line font-cond text-[12px] uppercase tracking-wider text-faint">
                    <tr>
                      <th className="px-3 py-1.5 font-semibold">{t("Adresse")}</th>
                      <th className="px-3 py-1.5 text-right font-semibold">{t("Depuis")}</th>
                      <th className="px-3 py-1.5 text-right font-semibold">{t("Débit")}</th>
                      <th className="px-3 py-1.5 text-right font-semibold">RTT</th>
                      <th className="px-3 py-1.5 text-right font-semibold" title={t("Paquets perdus vers le client sur la dernière seconde (total depuis la connexion)")}>{t("Pertes")}</th>
                      <th className="px-3 py-1.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {clients.map((c) => (
                      <tr key={c.id}>
                        <td className="px-3 py-1.5 font-mono text-[12px]">{c.address ?? "?"}</td>
                        <td className="num px-3 py-1.5 text-right whitespace-nowrap">
                          {c.since ? formatDuration((Date.now() - Date.parse(c.since)) / 1000) : "–"}
                        </td>
                        <td className="num px-3 py-1.5 text-right whitespace-nowrap">{formatNumber(c.send_mbps)} Mb/s</td>
                        <td className="num px-3 py-1.5 text-right whitespace-nowrap">{formatNumber(c.rtt_ms)} ms</td>
                        <td className={`num px-3 py-1.5 text-right whitespace-nowrap ${c.loss_pct > 0 ? "text-warn" : ""}`}>
                          {formatNumber(c.loss_pct)} % <span className="text-faint">({c.loss_total})</span>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {can("operator") && <Button size="sm" variant="ghost" onClick={() => kick(c.id)}>{t("Déconnecter")}</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
    <ConfirmDialog open={renewing} title={t("Changer la clé du client ?")} confirmLabel={t("Nouvelle clé")} busy={busy}
        onCancel={() => setRenewing(false)} onConfirm={renew}>
        {t("Les clients connectés à « {name} » sont déconnectés et l'ancienne clé est refusée. Il faudra transmettre le nouveau streamid au client.", { name: output.name })}
    </ConfirmDialog>
  </>);
}
